import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, clientApi, formatError } from "@/lib/api";
import { Stamp, Field, SectionLabel } from "@/components/Doc";
import { Step1, Step2, Step3, Step4, Step5, Step6 } from "@/components/AuthorizeSteps";

const ResumeForm = ({ message }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const go = async (e) => { e.preventDefault(); await api.post("/client/magic-link", { email }); setSent(true); };
  return (
    <div className="max-w-md mx-auto bg-paper border border-carbon p-8 shadow-[6px_6px_0_0_#D9C9A8]" data-testid="authorize-resume">
      <span className="t-label text-olive">Form LH-40 — Resume</span>
      <h1 className="t-head text-3xl mt-2">Authorization gate</h1>
      {message && <p className="t-body text-base text-stamp mt-4">{message}</p>}
      <p className="t-body text-base mt-4 text-olive">Enter the work email you used at checkout and we'll send a fresh link. Progress is saved.</p>
      {sent ? <p className="t-body text-lg mt-6" data-testid="magic-sent">If that address is on file, a link is on its way.</p> : (
        <form onSubmit={go} className="mt-6 flex flex-col gap-5">
          <Field label="Work email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="magic-email" />
          <button className="btn self-start" data-testid="magic-submit">Send the link</button>
        </form>
      )}
    </div>
  );
};

export default function Authorize() {
  const [params] = useSearchParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const t = params.get("token");
    if (t) sessionStorage.setItem("lh_client_token", t);
    if (!sessionStorage.getItem("lh_client_token")) { setError(""); setState(false); return; }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const { data } = await clientApi().get("/authorize/state");
      setState(data);
      const next = [1, 2, 3, 4, 5, 6].find((s) => !data.completedSteps.includes(s)) || 6;
      setStep(next);
    } catch (e) {
      if (e.response?.status === 401) { sessionStorage.removeItem("lh_client_token"); setError(formatError(e)); setState(false); }
      else toast.error(formatError(e));
    }
  };

  const advance = async () => { await load(); };

  if (state === null) return <main className="min-h-[60vh] flex items-center justify-center t-label text-olive">Pulling the file…</main>;
  if (state === false) return <main className="px-5 py-24" data-testid="authorize-page"><ResumeForm message={error} /></main>;

  if (done || state.client.status !== "PENDING_AUTHORIZATION") {
    const stop = done?.stopWord || state.client.stopWord;
    const phone = done?.emergencyPhone || state.client.emergencyPhone;
    return (
      <main className="px-5 sm:px-8 py-20" data-testid="authorize-page">
        <div className="settle max-w-2xl mx-auto bg-paper border border-carbon p-8 sm:p-12 relative shadow-[6px_6px_0_0_#D9C9A8]" data-testid="authorization-received">
          <div className="absolute -top-6 right-8"><Stamp color="red" rotate={-6} size="lg">Received</Stamp></div>
          <SectionLabel>Form LH-40 — {state.client.status.replace("_", " ")}</SectionLabel>
          <h1 className="t-head text-4xl mt-6">Authorization received.</h1>
          <p className="t-body text-lg mt-6 leading-[1.65]">We'll review your scope within one business day and reach out to book a thirty-minute calibration call. Testing begins after that call. Your card will not be charged until testing begins.</p>
          {stop && <p className="t-body text-lg mt-4 leading-[1.65]">Your stop-word is <span className="inline-block border-2 border-stamp text-stamp px-3 py-1 t-head text-2xl align-middle mx-1" data-testid="final-stop-word">{stop}</span>. Say it or send it to <span className="t-mono">{phone}</span> at any hour and every test stops within fifteen minutes.</p>}
          {state.client.status !== "PENDING_AUTHORIZATION" && state.client.status !== "IN_REVIEW" && <p className="t-label text-olive mt-6">Current status: {state.client.status}</p>}
        </div>
      </main>
    );
  }

  const props = { state, onDone: advance };
  return (
    <main className="min-h-[calc(100vh-4rem)] px-5 sm:px-8 py-16 sm:py-24" data-testid="authorize-page">
      <div className="max-w-3xl mx-auto mb-10 bg-manila border border-rule px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="authorize-banner">
        <span className="t-label text-carbon">Authorization gate — {state.client.legalName}</span>
        <span className="t-label text-olive">Subscription created · not billing · {state.completedSteps.length} of 6 steps filed</span>
      </div>
      {state.authorization?.changesNote && step === 1 && <p className="max-w-3xl mx-auto mb-8 t-body text-base text-stamp" data-testid="changes-note">Change requested by Longhand: {state.authorization.changesNote}</p>}
      {step === 1 && <Step1 {...props} />}
      {step === 2 && <Step2 {...props} />}
      {step === 3 && <Step3 {...props} />}
      {step === 4 && <Step4 {...props} onBack={() => setStep(3)} />}
      {step === 5 && <Step5 {...props} />}
      {step === 6 && <Step6 onDone={(d) => setDone(d)} />}
      {step > 1 && step !== 4 && <div className="max-w-3xl mx-auto mt-8"><button onClick={() => setStep(step - 1)} className="t-label text-olive hover:text-carbon" data-testid="authorize-back">← Previous step</button></div>}
    </main>
  );
}
